import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import clsx from 'clsx';
import { Select } from 'radix-ui';
import {
  Controller,
  type UseFieldArrayUpdate,
  useFormContext,
  type UseFormSetValue,
} from 'react-hook-form';
import Creatable from 'react-select/creatable';

import Canada from '@quick/assets/countries/canada.svg';
import Chile from '@quick/assets/countries/chile.svg';
import Colombia from '@quick/assets/countries/colombia.svg';
import Ecuador from '@quick/assets/countries/ecuador.svg';
import Mexico from '@quick/assets/countries/mexico.svg';
import Peru from '@quick/assets/countries/peru.svg';
import USA from '@quick/assets/countries/usa.svg';
import { FloatingLabel, WrappedInput } from '@quick/components/Forms';
import { InfoHover } from '@quick/components/InfoHover';
import type {
  PaginatedPointsOfInterest,
  PointsOfInterestFilters,
} from '@quick/go-api-types/geo/points_of_interest';
import type { ZipCodePaginated } from '@quick/go-api-types/geo/zip-codes';
import type { ICity } from '@quick/go-api-types/services/cities';
import type { IFavAddressList } from '@quick/go-api-types/users/addresses';
import { cn, filterUniqueObjectsArrayByIdProp } from '@quick/utils';

import { useAppTranslation } from '@/app/i18n/client';
import { fixBlur, ReactSelect as RSelect } from '@/components/core/ReactSelect';
import { useCountry } from '@/layers/core/deal/services/CountryContext';
import { useConfirmAddress } from '@/layers/core/deal/services/useConfirmAddress';
import { useDebounce } from '@/layers/core/deal/services/useDebounce';
import { useFetch } from '@/layers/core/deal/services/useFetch';
import { capitalize } from '@/layers/core/deal/utilities/capitalize';
import { onlyNumbers, nonNumbers } from '@/layers/core/helpers/regex';

import type { IForm } from '../types';

type AddressOpt = {
  value: `${string}.${number}`;
  label: string;
  city_id: number;
} & (
  | { zip: string }
  | { poi: string; latitude: number; longitude: number }
  | {
      fav: string;
      description: string;
      //latitude: number; longitude: number
    }
);

function getKey<T extends string>(index: number, val: T) {
  return `stops.${index}.${val}` as const;
}

function sortFavs(a: IFavAddressList, b: IFavAddressList) {
  if (!a.name.trim()) return a.address.trim().localeCompare(b.address.trim());
  return `${capitalize(a.name.trim())}: ${a.address.trim()}`.localeCompare(
    `${capitalize(b.name.trim())}: ${b.address.trim()}`
  );
}

export function Search({
  index,
  modal,
  updateAddress,
  user_country_id,
  user_id,
  countries = [],
}: {
  index: number;
  modal?: boolean;
  updateAddress: UseFieldArrayUpdate<IForm, 'stops'>;
  user_id: number | undefined;
  user_country_id: number | undefined;
  countries?: IdName[];
}) {
  const { t } = useAppTranslation(['common', 'ServicesOpPl', 'demand']);
  const { id: countryID } = useCountry();
  const { getValues, setValue, control, watch } = useFormContext<IForm>();

  const [city_id, taskAddress, cityCountryId] = watch([
    getKey(index, 'city_id'),
    getKey(index, 'address'),
    getKey(index, 'country_id'),
  ]);

  const countriesMap = new Map(countries.map((c) => [c.id, c]));
  // search first by country id of the stop, then by website country, then by user country
  const countryId =
    countriesMap.get(cityCountryId || 0)?.id ?? // should not find 0
    countriesMap.get(countryID)?.id ??
    countriesMap.get(user_country_id || 0)?.id ?? // should not find 0
    countries?.[0]?.id;

  const [citySearch, setCitySearch] = useState('');

  const debouncedSearch = useDebounce(citySearch.trim(), 1000);
  const { data: searchedCities } = useFetch<ICity[]>(
    debouncedSearch.length >= 3 && {
      url: '/services/cities',
      params: {
        city: debouncedSearch,
        country_id: countryId,
      },
    },
    { keepPreviousData: true }
  );

  const { data: favorites } = useFetch<IFavAddressList[]>({
    url: '/users/addresses',
    params: { pwa: true, user_id },
    auth: true,
  });

  const debouncedAddress = useDebounce(taskAddress.trim(), 500);

  const { data: selectedCity } = useFetchSelectedCity({
    city_id,
    index,
    setCitySearch,
    setValue,
  });
  const allCities = useMemo(
    () =>
      filterUniqueObjectsArrayByIdProp([
        ...(selectedCity || []),
        ...(searchedCities || []),
      ]) ?? [],
    [searchedCities, selectedCity]
  );

  const isUsZipCode = usZipCodeRegEx.test(debouncedAddress);
  const couldBeZipCode = onlyNumbers.test(debouncedAddress);

  const searchZipCode =
    debouncedAddress.length > 3 && (isUsZipCode || couldBeZipCode);

  const { data: zipCodes } = useFetch<ZipCodePaginated>(
    searchZipCode && {
      url: '/geo/zip-codes',
      params: {
        pagination: true,
        active: true,
        page_size: 10,
        zip: debouncedAddress,
        country_id: countryId,
      },
    },
    {
      keepPreviousData: true,
    }
  );

  const { data: pointsOfInterest } = useFetch<PaginatedPointsOfInterest>(
    debouncedAddress.length > 3 && {
      url: '/geo/points_of_interest',
      params: {
        active: true,
        name: debouncedAddress,
        country_id: countryId,
      } satisfies PointsOfInterestFilters,
    },
    {
      keepPreviousData: true,
    }
  );

  const addressOptions: AddressOpt[] = useMemo(() => {
    // #region favorites
    const flattenedFavorites = favorites?.flat() ?? [];
    const favoritesWithName =
      flattenedFavorites
        .filter(
          ({ name, city_id, latitude, longitude }) =>
            city_id?.toString()?.trim() && name && latitude && longitude
        )
        .toSorted(sortFavs)
        .map(
          (f): AddressOpt => ({
            value: `f.${f.id}`,
            label: `${capitalize(f.name.trim())}: ${f.address.trim()}`,
            fav: f.address.trim(),
            city_id: f.city_id ?? 0,
            description: f.description,
            //latitude: Number(f.latitude),
            //longitude: Number(f.longitude),
          })
        ) ?? [];
    const favoritesWithoutName =
      flattenedFavorites
        .filter(({ name, city_id }) => city_id?.toString()?.trim() && !name)
        .toSorted(sortFavs)
        .map(
          (f): AddressOpt => ({
            value: `f.${f.id}`,
            label: f.address.trim(),
            fav: f.address.trim(),
            city_id: f.city_id ?? 0,
            description: f.description,
            //latitude: Number(f.latitude),
            //longitude: Number(f.longitude),
          })
        ) ?? [];
    // #endregion

    // #region zip
    const zipCodeOptions =
      zipCodes?.data.map(
        (zip): AddressOpt => ({
          value: `z.${zip.id}`,
          city_id: zip.city_id,
          label: `${zip.zip}, ${zip.place}`,
          zip: zip.zip,
        })
      ) ?? [];
    // #endregion

    // #region POI
    const poiOptions =
      pointsOfInterest?.data
        .filter((v) => v.address)
        .map(
          (poi): AddressOpt => ({
            value: `p.${poi.id}`,
            label: `${poi.name}: ${poi.address}, ${poi.city_name}`,
            poi: poi.address,
            city_id: poi.city_id,
            latitude: poi.latitude,
            longitude: poi.longitude,
          })
        ) ?? [];
    // #endregion

    return [
      ...favoritesWithName,
      ...favoritesWithoutName,
      ...zipCodeOptions,
      ...poiOptions,
    ];
  }, [favorites, zipCodes, pointsOfInterest]);

  const isZipCodeSelected = zipCodes?.data.some(
    (z) => z.zip === taskAddress && z.city_id === city_id
  );

  const prevCityIdRef = useRef<string | number | null>(null);
  const [cityChanged, setCityChanged] = useState(false);
  useEffect(() => {
    const prev = prevCityIdRef.current;
    const changed = prev !== null && String(prev) !== String(city_id);
    setCityChanged(changed);
    prevCityIdRef.current = city_id;
  }, [city_id]);

  const {
    data: confirmedAddress,
    isLoading: isConfirmingAddress,
    error: errorConfirmingAddress,
  } = useConfirmAddress({
    index,
    debouncedAddress,
    shouldConfirm:
      !!Number(city_id) && (debouncedAddress.length > 0 || cityChanged),
    city_id,
    setValue,
  });

  const confirmedAddressZipCode = confirmedAddress?.zip_code?.replaceAll(
    nonNumbers,
    ''
  );

  return (
    <>
      <div className="flex min-w-0 flex-1 flex-row flex-nowrap gap-2">
        {countries.length > 0 && (
          <Controller
            control={control}
            name={`stops.${index}.country_id`}
            render={({ field: { onChange, value, name } }) => (
              <CountrySelector
                name={name}
                value={value ?? countryId}
                onChange={(value) => {
                  const id = Number(value);
                  if (!id) return;
                  onChange(Number(value));
                  updateAddress(index, {
                    ...getValues(`stops.${index}`),
                    country_id: Number(value),
                    city_id: '',
                  });
                }}
                countries={countries}
                className="w-10 basis-10"
              />
            )}
          />
        )}
        <label className="relative min-w-0 flex-grow md:flex-1">
          <FloatingLabel
            as="span"
            isOnModal={modal}
            label={t(
              (countryId || user_country_id || countryID) === 4
                ? 'demand:addresses.addressForm.zipCodeAddressLocation'
                : 'demand:addresses.addressForm.locationAddressPlaceholder'
            )}
          />
          <Controller
            control={control}
            name={`stops.${index}.address`}
            rules={{
              required: t('common:msg.required_field'),
            }}
            render={({
              field: { onChange, value, ...field },
              fieldState: { error },
            }) => (
              <Creatable
                {...field}
                onBlur={fixBlur(field.onBlur)}
                id={`stops.${index}.address`}
                inputId={`stops.${index}.address`}
                classNamePrefix="rs"
                className={clsx(
                  'rs-container h-full min-w-0 rounded',
                  !modal && 'nomodal'
                )}
                classNames={{
                  control: () =>
                    error?.message
                      ? `shadow-[inset_0px_0px_0px_1.5px_theme(colors.red.300)]
                    dark:shadow-[inset_0px_0px_0px_1.5px_theme(colors.red.500)]`
                      : '',
                }}
                inputValue={value || ''}
                onInputChange={(e, a) => {
                  const inputValue =
                    a.action === 'input-change' ? e : a.prevInputValue;
                  onChange(inputValue);
                  setValue(`stops.${index}.latitude`, undefined);
                  setValue(`stops.${index}.longitude`, undefined);

                  const zipCode = zipCodes?.data.find(
                    (z) => z.zip === inputValue
                  );
                  if (zipCode) {
                    setValue(`stops.${index}.city_id`, zipCode.city_id, {
                      shouldDirty: true,
                    });
                  }
                }}
                options={addressOptions}
                openMenuOnFocus
                onChange={(v) => {
                  if (!v?.value) return;
                  const city_id = v.city_id;
                  if ('zip' in v)
                    updateAddress(index, {
                      ...getValues(`stops.${index}`),
                      address: v.zip,
                      city_id,
                    });
                  else if ('fav' in v)
                    updateAddress(index, {
                      ...getValues(`stops.${index}`),
                      address: v.fav,
                      description: v.description,
                      // TODO: Uncomment when we can put a map below every address or something like that, so the user can be sure where the point is
                      //latitude: v.latitude,
                      //longitude: v.longitude,
                      city_id,
                    });
                  else if ('poi' in v)
                    updateAddress(index, {
                      ...getValues(`stops.${index}`),
                      address: v.poi,
                      latitude: v.latitude,
                      longitude: v.longitude,
                      city_id,
                    });
                }}
                placeholder={t('common:select')}
                styles={{
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 50,
                  }),
                  control: (provided) => ({ ...provided, height: '100%' }),
                }}
                filterOption={(opt, input) => {
                  if (opt.label.includes('Create')) return false;
                  if (
                    'zip' in opt.data &&
                    opt.data.zip === input &&
                    city_id === opt.data.city_id
                  )
                    return false;
                  return opt.label.toLowerCase().includes(input.toLowerCase());
                }}
                noOptionsMessage={() => null}
              />
            )}
          />
        </label>
      </div>
      <div
        className={clsx(
          'flex min-w-0 flex-1 flex-row flex-nowrap',
          !!confirmedAddressZipCode && !errorConfirmingAddress && 'gap-2'
        )}
      >
        <label className="relative min-w-0 flex-grow transition-[width] duration-300 md:flex-1">
          <FloatingLabel as="span" label={t('common:city')} isOnModal={modal} />
          <Controller
            control={control}
            name={`stops.${index}.city_id`}
            rules={{
              required: t('common:msg.required_field'),
              validate: (e) => {
                if (e === 0) return t('common:msg.required_field');
                return !errorConfirmingAddress;
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <RSelect
                {...field}
                value={allCities.find(
                  ({ id }) => id.toString() === field?.value?.toString()
                )}
                onChange={(e) => {
                  field.onChange(e?.id ?? '');
                  setValue(
                    `stops.${index}.city_name`,
                    `${e?.name}, ${e?.country_state.name}, ${e?.country_state.country.name}`
                  );
                }}
                className={clsx(
                  'rs-container h-full min-w-0 rounded',
                  !modal && 'nomodal'
                )}
                classNames={{
                  control: () =>
                    error?.message
                      ? `shadow-[inset_0px_0px_0px_1.5px_theme(colors.red.300)]
                    dark:shadow-[inset_0px_0px_0px_1.5px_theme(colors.red.500)]`
                      : '',
                }}
                classNamePrefix="rs"
                // set as read only when zip code is selected
                isSearchable={!isZipCodeSelected}
                isClearable={!isZipCodeSelected}
                menuIsOpen={isZipCodeSelected ? false : undefined}
                //
                openMenuOnFocus
                onInputChange={(v, meta) => {
                  if (
                    // change search input only on user input so we don't trigger a new search on select, blur, etc.
                    meta.action === 'input-change'
                  )
                    setCitySearch(v);
                }}
                styles={{
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 50,
                  }),
                  control: (provided) => ({ ...provided, height: '100%' }),
                }}
                options={allCities}
                placeholder={t('common:select')}
                getOptionValue={(c) => `${c.id}`}
                getOptionLabel={(c) =>
                  capitalize(
                    `${c.name}, ${c.country_state.name}, ${c.country_state.country.name}`
                  )
                }
                isLoading={!searchedCities && citySearch.trim().length >= 3}
                components={{ IndicatorSeparator: () => null }}
                noOptionsMessage={({ inputValue }) => {
                  if (inputValue.trim().length < 3)
                    return t('common:msg.writeSearch');
                  return t('common:msg.noResults');
                }}
              />
            )}
          />
        </label>
        {!isZipCodeSelected && (
          <Controller
            control={control}
            name={`stops.${index}.zip`}
            shouldUnregister
            render={({ field: { onChange, value, ...field } }) => (
              <div
                className={clsx(
                  'relative h-full',
                  'transition-[width,opacity] duration-300',
                  value ? 'w-28 md:w-24 lg:w-28' : 'w-0 [&>*]:hidden',
                  isConfirmingAddress && 'opacity-65'
                )}
              >
                <WrappedInput
                  {...field}
                  readOnly
                  label={t('demand:addresses.addressForm.zipCode')}
                  value={value ?? ''}
                  className="h-full"
                  isOnModal={modal}
                />
                <InfoHover
                  triggerClassName="absolute right-0 top-0 z-10 -translate-y-1/2 translate-x-1/2
                 bg-background-group dark:bg-background-group-dark rounded-full"
                >
                  {t('demand:addresses.addressForm.zipCodeInfo')}
                </InfoHover>
              </div>
            )}
          />
        )}
      </div>
    </>
  );
}

const icons: Record<number, SVGType | undefined> = {
  1: Colombia,
  2: Chile,
  3: Mexico,
  4: USA,
  70: Peru,
  71: Ecuador,
  72: Canada,
};

export function CountrySelector({
  className,
  countries,
  value,
  onChange,
  name,
}: {
  className?: string;
  countries: IdName[];
  value: number;
  onChange: (country: string) => void;
  name: string;
}) {
  const { t } = useAppTranslation(['common']);
  if (countries.length <= 1) return null;

  return (
    <Select.Root value={String(value)} onValueChange={onChange} name={name}>
      <Select.Trigger
        className={cn(
          `hover:text-brand focus:text-brand cs flex items-center justify-center gap-1
          hover:outline-none focus:outline-none dark:text-white`,
          className
        )}
        aria-label={t('common:country')}
      >
        <Select.Value className="flex text-red-500" />
        <Select.Icon className="self-center text-xs" />
      </Select.Trigger>

      <Select.Portal>
        <Select.Content
          className="z-100 overflow-hidden rounded bg-gray-50 text-gray-600 shadow dark:bg-gray-600
            dark:text-gray-200"
        >
          <Select.ScrollUpButton></Select.ScrollUpButton>
          <Select.Viewport className="flex flex-col gap-1 p-1">
            {countries.map(({ id, name }) => {
              const Icon = icons[id];
              if (!Icon) return null;

              return (
                <Select.Item
                  value={String(id)}
                  key={id}
                  className="relative flex rounded p-1 pr-5 hover:bg-gray-200 hover:outline-none
                    focus:bg-gray-200 focus:outline-none dark:hover:bg-gray-800
                    dark:focus:bg-gray-800"
                >
                  <Select.ItemText className="inline-flex">
                    <span className="flex items-center gap-2 font-semibold">
                      <Icon className="inline-block size-5" aria-hidden />
                      <span className="sr-only">{name}</span>
                    </span>
                  </Select.ItemText>
                  <Select.ItemIndicator>
                    <span className="bg-brand absolute right-1 top-1/2 size-2 -translate-y-1/2 rounded-full" />
                  </Select.ItemIndicator>
                </Select.Item>
              );
            })}
          </Select.Viewport>
          <Select.SelectScrollDownButton />
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}

const useFetchSelectedCity = ({
  city_id,
  index,
  setCitySearch,
  setValue,
}: {
  city_id: string | number;
  index: number;
  setCitySearch: Dispatch<SetStateAction<string>>;
  setValue: UseFormSetValue<IForm>;
}) => {
  const swr = useFetch<ICity[]>(
    !!city_id && {
      url: '/services/cities',
      params: {
        city_id,
      },
    }
  );
  // use effect instead of on success because swr deduplicates the request and swr only runs once,
  // so multiple stops with the same city_id will not work as expected
  useEffect(() => {
    if (swr.data) {
      const city = swr.data.find((c) => String(c.id) === String(city_id));
      if (!city) return;

      if (city?.name) setCitySearch(city.name);
      // TODO: Back is calculating this. Should we send it?
      setValue(
        `stops.${index}.city_name`,
        [
          city?.name,
          city?.country_state.name,
          city?.country_state.country.name,
        ].join(', ')
      );
      setValue(`stops.${index}.country_id`, city?.country_state.country.id);
    }
  }, [swr.data, setValue, index, setCitySearch, city_id]);
  return swr;
};

const usZipCodeRegEx = /^\d{5}(?:-\d{4})?$/;
